import { useQuery } from "@tanstack/react-query";
import { CloudFog } from "lucide-react";
import { type ComponentType, useMemo } from "react";

import { api } from "../../api/client";
import { Badge } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { useWeatherOptions } from "../../views/WeatherField";
import { visibilityText } from "../../weather";
import { revealPaint, revealStyle, useFadeIn } from "../reveal";
import { displayedDraft, terrainInputs } from "../terrain/terrainWeather";
import { FRIEND_BLUE } from "../units/symbols";
import { useOpenZone } from "../zones/useZones";
import { referenceLat, RING_WIDTH, ringRadius, visibilityState } from "./visibility";

/** Calque « Visibilité » du groupe « Météo » (conception validée par Alexis le 31/10/2026, `_visibilite/CONCEPTION.md`) :
 *  rien n'est tracé par beau temps ; par visibilité réduite, un voile gris sur le camp ou le terrain libre (sous les
 *  graphiques tactiques), une pastille « Visibilité » à son coin sud-ouest (le nord-ouest porte souvent les
 *  étiquettes des LIMA) et, dans la source des unités, un anneau
 *  par unité (`VisibilityRings`, premier enfant de la source « gx-units » : il suit les unités montrées, leurs
 *  glissements d'un temps à l'autre et leur opacité). Météo appliquée du dossier affiché, brouillon compris
 *  (`terrainInputs` : « auto » en CENTAC), lue par la même requête que le réglage Météo. Décoché par défaut. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
}

const BELOW = "graphics-fill";
const FADE_MS = 450;
/** Rouge de l'ennemi des anneaux (celui des portées de l'analyse du terrain). */
export const ENEMY_RING_RED = "#C83E3E";
/** Gris du voile posé sur le camp ou le terrain libre. */
export const VEIL_GREY = "#7b8494";

/** Météo appliquée du dossier affiché et état du calque (`null` tant qu'elle n'est pas connue). */
export function useVisibility() {
  const mapSlug = useUi((s) => s.mapSlug);
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises?.find((e) => e.id === exerciseId);
  const inputs = terrainInputs(exercise, displayedDraft(exerciseId, exercise));
  const { options } = useWeatherOptions({
    mapSlug,
    startDtg: inputs.start_dtg,
    timeZone: inputs.time_zone,
    scenario: inputs.weather_scenario,
    custom: inputs.weather_custom,
    waiting: !exerciseId,
  });
  return visibilityState(options);
}

/** Emprise [ouest, sud, est, nord] du camp ou du terrain libre affiché. */
function useExtent(): readonly number[] | null {
  const mapSlug = useUi((s) => s.mapSlug);
  const { zone } = useOpenZone();
  const { data: detail } = useQuery({ queryKey: ["map", mapSlug], queryFn: () => api.map(mapSlug!), enabled: Boolean(mapSlug) });
  return zone?.bbox ?? detail?.bbox ?? null;
}

/** Unités montrées, amies et ennemies, hors unités des niveaux inférieurs (les repères discrets `ghost` sont
 *  transparents). */
const RING_FILTER = ["all", ["!=", ["get", "sub"], true], ["match", ["get", "side"], ["AMI", "ENI"], true, false]];

/** Anneaux de visibilité réduite, dans la source des unités (`source` posé par elle) : trait plein de 1,25 px, sans
 *  remplissage, bleu ami au rayon de la vue amie, rouge au rayon du tir direct ennemi ; rien par beau temps. La couche
 *  reste montée (masquée) : créée avec la source, avant les symboles, elle reste dessous quand elle paraît. */
export function VisibilityRings({ Layer, source }: { Layer: ComponentType<any>; source?: string }) {
  const visible = useUi((s) => s.controlVisible.visibility === true);
  const state = useVisibility();
  const lat = referenceLat(useExtent());
  const friendly = visible && state?.reduced ? state.friendlyKm : null;
  const enemy = visible && state?.reduced ? state.enemyKm : null;
  const on = friendly !== null && enemy !== null;
  // Rendu à chaque image des glissements d'unités : style mémorisé, recalculé seulement quand les portées changent.
  const layout = useMemo(() => ({ visibility: on ? "visible" : "none" }), [on]);
  const paint = useMemo(
    () => ({
      "circle-radius": on ? ringRadius(friendly!, enemy!, lat) : 0,
      "circle-color": "rgba(0,0,0,0)",
      "circle-stroke-width": RING_WIDTH,
      "circle-stroke-color": ["match", ["get", "side"], "ENI", ENEMY_RING_RED, FRIEND_BLUE],
      "circle-stroke-opacity": ["*", 0.9, ["get", "opacity"], ["-", 1, ["coalesce", ["get", "ghost"], 0]]],
      "circle-pitch-alignment": "map",
    }),
    [on, friendly, enemy, lat],
  );
  return <Layer id="visibility-rings" type="circle" source={source} filter={RING_FILTER} layout={layout} paint={paint} />;
}

/** Voile gris du camp ou du terrain libre (couche posée sur la source de la limite du camp, sous les graphiques) et
 *  pastille « Visibilité » au coin sud-ouest de l'emprise. */
export function VisibilityLayers({ Layer, Marker }: EngineParts) {
  const visible = useUi((s) => s.controlVisible.visibility === true);
  const state = useVisibility();
  const extent = useExtent();
  const reduced = Boolean(visible && state?.reduced);
  // Montée en fondu à chaque apparition (règle d'Alexis du 29/09/2026 : rien n'apparaît d'un coup).
  const shown = useFadeIn(reduced ? `${state?.friendlyKm}-${state?.enemyKm}` : null);
  if (!reduced || !state) return null;
  // Voile plus dense quand la vue est plus courte : 0,10 à 5 km, 0,18 sous 1 km.
  const opacity = Math.min(0.18, Math.max(0.1, 0.18 - ((state.friendlyKm ?? 0) - 1) * 0.02));
  return (
    <>
      <Layer
        id="visibility-veil"
        type="fill"
        source="gx-boundary"
        beforeId={BELOW}
        paint={revealPaint("fill", { "fill-color": VEIL_GREY, "fill-opacity": opacity }, shown, FADE_MS)}
      />
      {extent && state.range && (
        <Marker longitude={extent[0]} latitude={extent[1]} anchor="bottom-left" offset={[8, -8]} style={{ pointerEvents: "none" }}>
          <div style={revealStyle(shown, FADE_MS)}>
            <Badge tone="warn" className="shadow-[0_1px_2px_rgb(16_18_24/0.12)]">
              <CloudFog size={12} className="mr-1" aria-hidden />
              {t("Visibilité {range}", { range: visibilityText(state.range) })}
            </Badge>
          </div>
        </Marker>
      )}
    </>
  );
}
